<style lang="scss" scoped>
@import '$vars';

.medical-history {
  @apply flex flex-col items-start content-start justify-start;
  @apply mt-2 mb-6 pb-2;
  @apply w-full;

  &__header {
    @apply flex flex-row items-center justify-start;
    @apply w-full;
    @apply cursor-pointer;
    background: lighten($color-blue, 35);
  }

  &__date {
    @apply flex flex-col content-center items-center justify-center self-stretch;
    @apply py-1;
    background: $color-blue;
    width: 2.5rem;

    &-day {
      color: #ffffff;
      font-family: $font2;
      font-size: 1rem;
      font-weight: 500;
      line-height: 1;
      margin-bottom: 0.25rem;
    }

    &-month {
      color: #ffffff;
      font-size: 0.6rem;
      line-height: 1;
    }
  }

  &__bar {
    @apply flex flex-row items-center justify-start self-stretch;
    @apply px-3;
  }

  &__title {
    @apply m-0 p-0 mr-3;
    font-family: $font1;
    font-size: 0.8rem;
    font-weight: 700;
  }

  &__arrow {
    @apply flex items-center justify-center self-stretch;
    @apply px-3 ml-auto;
    color: $color-blue;

    & .d-inline {
      @apply mr-2;
    }

    i.icon-arrow-down {
      transition: all 0.3s ease;

      &--up {
        transform: rotate(180deg);
        transition: all 0.3s ease;
      }
    }
  }

  &__content {
    @apply flex flex-col items-center content-center justify-start;
    @apply w-full ml-auto p-4 box-border;
    border-bottom: 1px solid rgb(225, 225, 225);
    border-left: 1px solid rgb(225, 225, 225);
    border-right: 1px solid rgb(225, 225, 225);
    margin-right: 1px;
    min-height: 500px;
    overflow: auto;
    width: calc(100% - 2.5rem - 1px);
    transition: all 0.3s ease;
  }

  &__row {
    @apply flex flex-row items-center justify-start w-full mb-2;
  }

  &__period {
    @apply w-1/2 mr-auto;
  }

  &__actions {
    @apply flex flex-row;

    button {
      @apply ml-4;
    }
  }

  &__footer {
    // @apply block mt-1;
  }
}
</style>

<template>
  <article class="medical-history">
    <header
      @click="toggleOpen"
      class="medical-history__header"
    >
      <div class="medical-history__date">
        <div class="medical-history__date-day">
          {{ datetime.day }}
        </div>

        <div class="medical-history__date-month">
          {{ datetime.month }}/{{ datetime.year }}
        </div>
      </div>

      <div class="medical-history__bar">
        <h3 class="medical-history__title">
          Avaliação clínica
        </h3>

        <span
          v-if="evaluation['scheduling_type'] === 'RETORNO'"
          class="label label--yellow"
        >
          Retorno
        </span>
      </div>

      <div class="medical-history__arrow">
        <dr-loader
          v-if="ui.isLoading"
          class="d-inline"
        />

        <i
          v-if="!ui.isLoading"
          :class="{ 'icon-arrow-down--up': ui.isOpen }"
          class="icon icon-arrow-down"
        />
      </div>
    </header>

    <transition
      name="slide-down"
      mode="in-out"
    >
      <section
        v-if="ui.isOpen"
        class="medical-history__content"
      >
        <!-- Medical History Period + Action Buttons -->
        <div class="medical-history__row">
          <div class="medical-history__period">
            Duração da Consulta: <strong>{{ interval }}</strong>

            <!-- Show Edit Period Button -->
            <medical-history-edit-period
              v-if="showEditPeriod"
              :appointment-id="evaluation.id"
              :start-date-time="evaluation['medical_consultation_start']"
              :end-date-time="evaluation['medical_consultation_finish']"
              @new-interval="(value) => interval = value"
            />
          </div>

          <div class="medical-history__actions">
            <button
              v-if="ui.showResendFilesButton"
              :disabled="ui.isLoading"
              class="btn btn-blue appointment-simple__submit"
              @click="resendMedicalFiles"
            >
              Reenviar documentos
            </button>

            <button
              class="btn btn-primary appointment-simple__submit"
              @click="ui.showReceipt = true"
            >
              Ver recibo
            </button>
          </div>
        </div>

        <!-- Medical History Data -->
        <medical-history-data
          v-for="data in medicalHistoryData"
          :key="data.key"
          :title="data.title"
          :content="data.content"
        />

        <!-- Notes -->
        <medical-history-notes
          :appointment-id="evaluation.id"
        />

        <!-- Default Anamnesis -->
        <medical-history-anamnesis
          v-if="anamnesis.length > 0"
          :anamnesis="anamnesis"
        />

        <!-- Customized Anamnesis -->
        <template v-if="customAnamnesis.length > 0">
          <medical-history-anamnesis
            v-for="(anamnesis, index) in customAnamnesis"
            :key="`custom-anamnesis-${index}`"
            :anamnesis="anamnesis"
            :is-customized="true"
          />
        </template>

        <!-- Prescriptions -->
        <medical-history-prescriptions
          :is-medical-history-visible="isMedicalHistoryVisible"
          :appointment-started="true"
          :appointment-id="evaluation.id"
          :appointment="evaluation"
          :patient="patient"
        />

        <!-- Exams -->
        <medical-history-exams
          :is-medical-history-visible="isMedicalHistoryVisible"
          :appointment-started="true"
          :appointment-id="evaluation.id"
          :appointment="evaluation"
          :patient="patient"
        />

        <!-- Sick notes -->
        <medical-history-sick-notes
          :is-medical-history-visible="isMedicalHistoryVisible"
          :appointment-started="true"
          :appointment-id="evaluation.id"
          :appointment="evaluation"
          :patient="patient"
        />

        <!-- Files -->
        <medical-history-files
          :appointment-id="evaluation.id"
        />

        <!-- Download medical record -->
        <medical-history-download-record
          :appointment-id="evaluation.id"
          :patient.sync="patient"
        />
      </section>
    </transition>

    <transition name="fade">
      <receipt
        v-if="ui.showReceipt"
        :id="evaluation.id"
        @close="ui.showReceipt = false"
      />
    </transition>
  </article>
</template>

<script>
import Receipt from '@/components/layout/appointments/receipt'
import MedicalHistoryEditPeriod from './medicalHistory/medicalHistoryEditPeriod'
import MedicalHistoryData from './medicalHistory/medicalHistoryData'
import MedicalHistoryAnamnesis from './medicalHistory/medicalHistoryAnamnesis'
import MedicalHistoryNotes from './medicalHistory/medicalHistoryNotes'
import MedicalHistoryPrescriptions from './medicalHistory/medicalHistoryPrescriptions'
import MedicalHistoryExams from './medicalHistory/medicalHistoryExams'
import MedicalHistorySickNotes from './medicalHistory/medicalHistorySickNotes'
import MedicalHistoryFiles from './medicalHistory/medicalHistoryFiles'
import MedicalHistoryDownloadRecord from './medicalHistory/medicalHistoryDownloadRecord'

export default {
  name: 'MedicalHistoryEvaluation',

  components: {
    Receipt,
    MedicalHistoryEditPeriod,
    MedicalHistoryData,
    MedicalHistoryAnamnesis,
    MedicalHistoryNotes,
    MedicalHistoryPrescriptions,
    MedicalHistoryExams,
    MedicalHistorySickNotes,
    MedicalHistoryFiles,
    MedicalHistoryDownloadRecord
  },

  props: {
    evaluation: {
      type: Object,
      required: true
    },

    doctor: {
      type: Object,
      required: true
    },

    patient: {
      type: Object,
      required: true
    },

    hasAppointment: {
      type: Boolean,
      default: () => false
    },

    isMedicalHistoryVisible: {
      type: Object,
      default: () => ({ msg: null, status: true })
    }
  },

  data () {
    return {
      ui: {
        isOpen: false,
        isLoading: false,
        showReceipt: false,
        showResendFilesButton: false
      },
      interval: null,
      anamnesis: [],
      customAnamnesis: []
    }
  },

  computed: {
    datetime () {
      let interval = null
      const appointmentDate = this.$moment(this.evaluation['appointment_start'])

      if (this.evaluation['medical_consultation_finish']) {
        const appointmentStart = this.$moment(this.evaluation['medical_consultation_start'])
        const appointmentEnd = this.$moment(this.evaluation['medical_consultation_finish'])

        const diff = appointmentEnd.diff(appointmentStart, 'minutes') || 1

        const hours = Math.floor(diff / 60)
        const minutes = diff - (hours * 60)

        interval = `${
          hours < 10 ? '0' + hours : hours
        }h${
          minutes < 10 ? '0' + minutes : (minutes === 60 ? '00' : minutes)
        }min`
      }

      return {
        day: appointmentDate.format('D'),
        month: appointmentDate.format('MMM'),
        year: appointmentDate.format('YY'),
        interval: interval
      }
    },

    showEditPeriod () {
      return this.$moment(new Date()).diff(this.evaluation['appointment_finish'], 'hours') <= 24
    },

    medicalHistoryData () {
      const mainComplaint = {
        key: 'mainComplaint',
        title: 'Queixa principal',
        content: this.evaluation['main_complaint']
      }

      const diagnosticHypothesis = {
        key: 'diagnosticHypothesis',
        title: 'Hipótese diagnóstica',
        content: this.evaluation['diagnostic_hypothesis']
      }

      const finalDiagnostic = {
        key: 'finalDiagnostic',
        title: 'Diagnóstico final',
        content: this.evaluation['final_diagnostic']
      }

      const diagnosticAnalysis = {
        key: 'diagnosticAnalysis',
        title: 'Análise diagnóstica',
        content: this.evaluation['diagnostic_analysis']
      }

      return [
        mainComplaint,
        diagnosticHypothesis,
        finalDiagnostic,
        diagnosticAnalysis
      ].filter((item) => item.content)
    },

    isSpecialPatient () {
      return (this.$route.params.patientType === 'specialPatient')
    }
  },

  watch: {
    'datetime': {
      deep: false,
      immediate: true,
      handler (v) {
        if ('interval' in v) {
          this.interval = v.interval
        }
      }
    }
  },

  methods: {
    toggleOpen () {
      if (this.ui.isOpen) {
        this.ui.isOpen = false
        return
      }

      this.init()
      this.getMedicalFiles()
    },

    init () {
      const { id: appointmentId } = this.evaluation

      const anamnesis = this.getAnamnesis(appointmentId)
        .then((res) => (this.anamnesis = res))
        .catch(() => (this.anamnesis = []))

      const customAnamnesis = this.getCustomAnamnesis(appointmentId)
        .then((res) => (this.customAnamnesis = res))
        .catch(() => (this.customAnamnesis = []))

      this.fn_SHOW_PROGRESS()
      this.ui.isLoading = true

      Promise.all([anamnesis, customAnamnesis])
        .finally(() => {
          this.ui.isOpen = true
          this.ui.isLoading = false
          this.fn_HIDE_PROGRESS()
        })
    },

    async getAnamnesis (appointmentId = null) {
      if (appointmentId) {
        const agendaRes = await this.$http
          .get(`/doctor/agenda?filterAnd[a.id]=${appointmentId}`, this.$store.getters.getAuthHeader)

        const anamnesisId = agendaRes.data.data[0].anamnesisId

        const anamnesisRes = anamnesisId
          ? await this.$http.get(`/anamnesis/${anamnesisId}`, this.$store.getters.getAuthHeader)
          : ''

        if (
          ((this.isMedicalHistoryVisible.status && this.hasAppointment) || this.isSpecialPatient) &&
          anamnesisRes
        ) {
          return this.buildAnamnesisResponse(anamnesisRes.data)
        }

        return []
      }

      return []
    },

    async getCustomAnamnesis (appointmentId = null) {
      if (appointmentId) {
        const { data } = await this.$http.get(
          `/agenda/${appointmentId}/custom_anamnesis_question_answer?itensPerPage=100`,
          this.$store.getters.getAuthHeader
        )

        const answers = data.data

        const allAnamnesisIds = []

        answers.forEach(a => allAnamnesisIds.push(a['custom_anamnesis_question']['custom_anamnesis'].id))
        const anamnesisIds = [...new Set(allAnamnesisIds)]

        const customAnamnesis = []
        const promises = []

        for (const id of anamnesisIds) {
          const promise = this.$http.get(`/doctor/custom_anamnesis/${id}`, this.$store.getters.getAuthHeader)
            .then((res) => {
              const data = res.data
              const anamnese = {
                id: data.id,
                name: data.name,
                questions: []
              }
              data['custom_anamnesis_questions'].forEach((q) => {
                anamnese.questions.push({
                  id: q.id,
                  position: q.position,
                  name: q.name,
                  type: q['field_type'],
                  options: q['custom_anamnesis_question_options'],
                  value: null
                })
              })
              customAnamnesis.push(anamnese)
            })
          promises.push(promise)
        }

        await Promise.all(promises)
          .finally(() => {
            customAnamnesis.forEach((anamnese) => {
              anamnese.questions.forEach((question) => {
                const answer = (question.type !== 'CHECKBOX')
                  ? answers.find(a => String(a['custom_anamnesis_question'].id) === String(question.id))
                  : answers.filter(a => String(a['custom_anamnesis_question'].id) === String(question.id))

                switch (true) {
                  case (question.type === 'CHECKBOX'):
                    const checkboxOptions = []

                    answers.forEach((a) => {
                      const filtrado = question.options.find(o => String(o.id) === String(a.name))
                      filtrado && checkboxOptions.push(filtrado)
                    })

                    question.value = checkboxOptions.reduce((fullAnswer, currentAnswer) => {
                      if (fullAnswer === '') {
                        return `${currentAnswer.name}`
                      }
                      return `${fullAnswer}, ${currentAnswer.name}`
                    }, '')

                    break

                  case (question.type === 'RADIO'):
                  case (question.type === 'SELECT'):
                    question.value = question.options.find((option) => {
                      if (String(option.id) === String(answer.name)) {
                        return option.name
                      }
                    }).name
                    break

                  case (question.type === 'TEXT'):
                  case (question.type === 'TEXTAREA'):
                  case (question.type === 'NUMBER'):
                    question.value = answer.name
                    break

                  case (question.type === 'DATE'):
                    question.value = String(this.$moment(answer.name).format('L'))
                    break

                  default:
                    break
                }
              })
            })
          })

        return customAnamnesis
      }
      return []
    },

    buildAnamnesisResponse (response = null) {
      if (response) {
        const formattedResponse = []

        formattedResponse.push({ title: 'Problemas renais', value: response['kidney_problems'] })
        formattedResponse.push({ title: 'Problemas articulares ou reumatismo', value: response['joint_problems_or_rheumatism'] })
        formattedResponse.push({ title: 'Problemas cardí­acos', value: response['heart_problems'] })
        formattedResponse.push({ title: 'Problemas respiratórios', value: response['breathing_problems'] })
        formattedResponse.push({ title: 'Problemas gástricos', value: response['stomach_problems'] })
        formattedResponse.push({ title: 'Alergias', value: response['allergies'] })
        formattedResponse.push({ title: 'Hepatite', value: response['is_hepatitis'] ? 'Sim' : 'Não' })
        formattedResponse.push({ title: 'Gravidez', value: response['is_pregnancy'] ? 'Sim' : 'Não' })
        formattedResponse.push({ title: 'Diabetes', value: response['is_diabetes'] ? 'Sim' : 'Não' })
        formattedResponse.push({ title: 'Problemas de cicatrização', value: response['is_healing_problems'] ? 'Sim' : 'Não' })
        formattedResponse.push({ title: 'Faz uso de algum medicamento?', value: response['use_any_medication'] })

        return formattedResponse
      }
      return []
    },

    allSettled (promises) {
      const wrappedPromises = promises
        .map((p) => Promise.resolve(p).then(
          (val) => ({ status: 'fulfilled', value: val }),
          (err) => ({ status: 'rejected', reason: err })
        ))

      return Promise.all(wrappedPromises)
    },

    getMedicalFiles () {
      const urls = [
        `/sick_note?filterAnd[sn.agenda]=${this.evaluation.id}`,
        `/exam_request?filterAnd[er.agenda]=${this.evaluation.id}`,
        `/prescription?filterAnd[p.agenda]=${this.evaluation.id}`
      ]

      const promises = []

      urls.forEach((url) => {
        const request = new Promise((resolve) => {
          this.$http
            .get(url, this.$store.getters.getAuthHeader)
            .then((res) => {
              if (res.data.data.length > 0) {
                resolve(true)
                return
              }

              resolve(false)
            })
            .catch(() => resolve(false))
        })

        promises.push(request)
      })

      this.allSettled(promises)
        .then((res) => {
          if (res.some((i) => i.value)) {
            this.ui.showResendFilesButton = true
          }
        })
    },

    resendMedicalFiles () {
      const url = `/doctor/agenda/${this.evaluation.id}/agenda_resend_files`

      this.fn_SHOW_PROGRESS()
      this.ui.isLoading = true

      this.$http
        .post(url, {}, this.$store.getters.getAuthHeader)
        .then((res) => {
          this.$store.dispatch('setModalAlert', {
            title: 'Sucesso',
            body: 'Os documentos médicos foram reenviados ao paciente com sucesso.',
            size: 'md',
            active: true
          })
        })
        .catch((err) => this.fn_HANDLE_ERROR(err))
        .finally(() => {
          this.fn_HIDE_PROGRESS()
          this.ui.isLoading = false
        })
    }
  }
}
</script>
